import { useState } from 'react'
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, RadioGroup, FormControlLabel, Radio, FormLabel } from '@mui/material'
import { useT } from '@/i18n/useT'

interface ExportDialogProps {
  open: boolean
  onClose: () => void
  onExport: (config: { format: 'xlsx' | 'csv'; separator: 'comma' | 'period'; includeCustomColumns: boolean }) => void
}

export function ExportDialog({ open, onClose, onExport }: ExportDialogProps) {
  const { t } = useT()
  const [format, setFormat] = useState<'xlsx' | 'csv'>('xlsx')
  const [separator, setSeparator] = useState<'comma' | 'period'>('comma')
  const [includeCustomColumns, setIncludeCustomColumns] = useState(true)

  const handleExport = () => {
    onExport({ format, separator, includeCustomColumns })
  }

  const handleClose = () => {
    // Reset to defaults
    setFormat('xlsx')
    setSeparator('comma')
    setIncludeCustomColumns(true)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('Export database')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
            <FormLabel component="legend">{t('File format')}</FormLabel>
            <RadioGroup
              row
              value={format}
              onChange={(e) => setFormat(e.target.value as 'xlsx' | 'csv')}
            >
              <FormControlLabel value="xlsx" control={<Radio />} label="Excel (.xlsx)" />
              <FormControlLabel value="csv" control={<Radio />} label="CSV" />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
            <FormLabel component="legend">{t('Decimal separator')}</FormLabel>
            <RadioGroup
              row
              value={separator}
              onChange={(e) => setSeparator(e.target.value as 'comma' | 'period')}
            >
              <FormControlLabel
                value="comma"
                control={<Radio />}
                label={`${t('Comma')} (1.234,56)`}
              />
              <FormControlLabel
                value="period"
                control={<Radio />}
                label={`${t('Period')} (1,234.56)`}
              />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">{t('Include custom columns')}</FormLabel>
            <RadioGroup
              row
              value={includeCustomColumns.toString()}
              onChange={(e) => setIncludeCustomColumns(e.target.value === 'true')}
            >
              <FormControlLabel value="true" control={<Radio />} label={t('Yes')} />
              <FormControlLabel value="false" control={<Radio />} label={t('No')} />
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('Cancel')}</Button>
        <Button onClick={handleExport} variant="contained">
          {t('Export')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
